import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ListTodo, Clock, CheckCircle2, AlertCircle, Plus, Users, ExternalLink, Eye, Pencil, ShieldCheck, Package } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

// Import react-big-calendar CSS
import 'react-big-calendar/lib/css/react-big-calendar.css';

const API = '/api';

// Setup moment localizer for react-big-calendar with Indian date format (DD/MM/YYYY)
moment.locale('en-IN');
const localizer = momentLocalizer(moment);

// Custom date formats for react-big-calendar - using moment directly for DD/MM/YYYY
const calendarFormats = {
  dateFormat: 'D',
  dayFormat: (date) => moment(date).format('ddd DD/MM'),
  weekdayFormat: (date) => moment(date).format('dddd'),
  dayRangeHeaderFormat: ({ start, end }) =>
    `${moment(start).format('DD/MM/YYYY')} – ${moment(end).format('DD/MM/YYYY')}`,
  dayHeaderFormat: (date) => moment(date).format('dddd, DD/MM/YYYY'),
  monthHeaderFormat: (date) => moment(date).format('MMMM YYYY'),
  agendaDateFormat: (date) => moment(date).format('ddd DD/MM/YYYY'),
  agendaTimeFormat: (date) => moment(date).format('HH:mm'),
  agendaTimeRangeFormat: ({ start, end }) =>
    `${moment(start).format('HH:mm')} – ${moment(end).format('HH:mm')}`,
  agendaHeaderFormat: ({ start, end }) =>
    `${moment(start).format('DD/MM/YYYY')} – ${moment(end).format('DD/MM/YYYY')}`,
  eventTimeRangeFormat: ({ start, end }) =>
    `${moment(start).format('HH:mm')} – ${moment(end).format('HH:mm')}`,
  eventTimeRangeStartFormat: (date) => moment(date).format('HH:mm'),
  eventTimeRangeEndFormat: (date) => moment(date).format('HH:mm'),
  timeGutterFormat: (date) => moment(date).format('HH:mm'),
};

// Custom toolbar component
const CustomToolbar = ({ label, onNavigate, onView, view }) => {
  return (
    <div className="flex items-center justify-between mb-4 p-3 bg-stone-50 rounded-lg" data-testid="calendar-toolbar">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onNavigate('PREV')}>
          <ChevronLeft size={18} />
        </Button>
        <Button variant="outline" size="sm" onClick={() => onNavigate('TODAY')}>
          Today
        </Button>
        <Button variant="outline" size="sm" onClick={() => onNavigate('NEXT')}>
          <ChevronRight size={18} />
        </Button>
      </div>
      
      <h2 className="text-xl font-semibold text-stone-800">{label}</h2>
      
      <div className="flex gap-1">
        {['month', 'week', 'day', 'agenda'].map((v) => (
          <Button
            key={v}
            variant={view === v ? 'default' : 'outline'}
            size="sm"
            onClick={() => onView(v)}
            className={view === v ? 'bg-red-600 text-white' : ''}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </Button>
        ))}
      </div>
    </div>
  );
};

// Status color mapping
const statusColors = {
  not_started: { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' },
  in_progress: { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },
  completed: { bg: '#d1fae5', border: '#10b981', text: '#047857' },
  delayed: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c' },
  // Calendar event types (appointments & reminders)
  appointment: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  reminder: { bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' },
};

const FullCalendar = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Task detail dialog state (replaces edit-only dialog)
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  // Editable date fields inside task detail
  const [editStartDate, setEditStartDate] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [dateEditMode, setDateEditMode] = useState(false);
  
  // Create task dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createData, setCreateData] = useState({
    project_id: '',
    title: '',
    description: '',
    start_date: '',
    deadline: '',
    assigned_to: '',
  });
  
  // NEW: Create choice dialog state (for selecting Task/Appointment/Reminder)
  const [createChoiceOpen, setCreateChoiceOpen] = useState(false);
  const [selectedSlotDates, setSelectedSlotDates] = useState({ start: '', end: '' });
  
  // Calendar Event dialog state (Appointments & Reminders)
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventData, setEventData] = useState({
    title: '',
    event_type: 'appointment',
    start_datetime: '',
    end_datetime: '',
    project_id: '',
    task_id: '',
    notes: '',
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'staff', 'admin', 'project_manager'].includes(user.role);
  const isVendor = user.role === 'vendor' || user.role === 'labour';
  const isClient = user.role === 'client';

  // Fetch tasks, projects, vendors, and calendar events
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes, usersRes, eventsRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/calendar-events`).catch(() => ({ data: [] })),
      ]);
      
      let fetchedTasks = tasksRes.data;
      
      // Role-based filtering:
      // - Admin: All tasks
      // - Vendor: Only assigned tasks
      // - Client: Only tasks from their projects (backend already filters by client_id for projects)
      if (isVendor) {
        fetchedTasks = fetchedTasks.filter(t => 
          t.assigned_to === user.id || 
          (t.assigned_vendors && t.assigned_vendors.includes(user.id))
        );
      }
      // Client filtering is handled by backend (projects endpoint returns only client's projects)
      
      setTasks(fetchedTasks);
      setProjects(projectsRes.data);
      setCalendarEvents(eventsRes.data || []);
      // Filter to get only vendors (for admin task creation)
      if (isAdmin) {
        setVendors(usersRes.data.filter(u => u.role === 'vendor' || u.role === 'labour'));
      }
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isVendor, user.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Convert tasks and calendar events to display events
  const events = useMemo(() => {
    let filteredTasks = tasks;
    
    // Filter by project
    if (filterProject !== 'all') {
      filteredTasks = filteredTasks.filter(t => t.project_id === filterProject);
    }
    
    // Filter by status
    if (filterStatus !== 'all') {
      filteredTasks = filteredTasks.filter(t => t.status === filterStatus);
    }
    
    // Convert tasks to calendar display format
    const taskEvents = filteredTasks.map(task => {
      // End date (deadline) is the PRIMARY display date for execution monitoring
      const endDate = task.deadline ? new Date(task.deadline) : new Date();
      const startDate = task.start_date ? new Date(task.start_date) : new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const project = projects.find(p => p.id === task.project_id);
      const projectName = project?.name || 'Unknown';
      
      // Role-based title display:
      // - Admin: Full info
      // - Vendor: Task name + status (no other vendors)
      // - Client: Task name + progress (no vendor names, no rates)
      let displayTitle = task.title || task.task_title || 'Untitled';
      
      if (isAdmin) {
        displayTitle = `${displayTitle} (${projectName})`;
      } else if (isVendor) {
        displayTitle = `${displayTitle}`;
      } else if (isClient) {
        // Show task with progress if available
        const progress = task.progress_percentage || 0;
        displayTitle = progress > 0 ? `${displayTitle} (${progress}%)` : displayTitle;
        // Highlight if awaiting approval
        if (task.client_approval_status === 'pending') {
          displayTitle = `⚠️ ${displayTitle}`;
        }
      }
      
      return {
        id: task.id,
        title: displayTitle,
        start: startDate,
        end: endDate,
        allDay: true,
        resource: task,
        status: task.status || 'not_started',
        eventType: 'task',
      };
    });
    
    // Convert calendar events (appointments/reminders) to display format
    const appointmentEvents = calendarEvents
      .filter(e => e.status === 'active')
      .filter(e => filterProject === 'all' || !e.project_id || e.project_id === filterProject)
      .map(event => {
        const startDate = new Date(event.start_datetime);
        const endDate = event.end_datetime ? new Date(event.end_datetime) : startDate;
        
        // Add icon prefix based on event type
        const icon = event.event_type === 'reminder' ? '🔔' : '📅';
        let displayTitle = `${icon} ${event.title}`;
        
        // Add project name if linked
        if (event.project_id) {
          const project = projects.find(p => p.id === event.project_id);
          if (project) {
            displayTitle = `${displayTitle} (${project.name})`;
          }
        }
        
        return {
          id: event.id,
          title: displayTitle,
          start: startDate,
          end: endDate,
          allDay: false,
          resource: event,
          status: event.event_type, // Use event_type for styling
          eventType: 'calendar_event',
        };
      });
    
    return [...taskEvents, ...appointmentEvents];
  }, [tasks, calendarEvents, projects, filterProject, filterStatus, isAdmin, isVendor, isClient]);

  // Event style getter based on status
  const eventStyleGetter = useCallback((event) => {
    const colors = statusColors[event.status] || statusColors.not_started;
    return {
      style: {
        backgroundColor: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        color: colors.text,
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '12px',
        fontWeight: '500',
      },
    };
  }, []);

  // Handle event click - Show task or calendar event detail
  const handleSelectEvent = useCallback((event) => {
    if (event.eventType === 'calendar_event') {
      // Handle calendar event (appointment/reminder)
      setSelectedEvent(event.resource);
      setEventDetailOpen(true);
    } else {
      // Handle task
      const task = event.resource;
      setSelectedTask(task);
      // Initialize editable date fields
      setEditStartDate(task.start_date ? task.start_date.split('T')[0] : '');
      setEditDeadline(task.deadline ? task.deadline.split('T')[0] : '');
      setDateEditMode(false); // Start in view mode
      setTaskDetailOpen(true);
    }
  }, []);

  // Navigate to full task page
  const navigateToTask = () => {
    if (selectedTask) {
      // Navigate to tasks page with task highlighted
      navigate(`/tasks?project=${selectedTask.project_id}&task=${selectedTask.id}`);
      setTaskDetailOpen(false);
    }
  };
  
  // Handle create calendar event (appointment/reminder)
  const handleCreateCalendarEvent = async () => {
    if (!eventData.title) {
      toast.error('Please enter a title');
      return;
    }
    if (!eventData.start_datetime) {
      toast.error('Please select a date and time');
      return;
    }
    
    try {
      await axios.post(`${API}/calendar-events`, {
        title: eventData.title,
        event_type: eventData.event_type,
        start_datetime: eventData.start_datetime,
        end_datetime: eventData.end_datetime || eventData.start_datetime,
        project_id: eventData.project_id || null,
        task_id: eventData.task_id || null,
        notes: eventData.notes || null,
      });
      
      toast.success(`${eventData.event_type === 'reminder' ? 'Reminder' : 'Appointment'} created!`);
      setEventDialogOpen(false);
      setEventData({
        title: '',
        event_type: 'appointment',
        start_datetime: '',
        end_datetime: '',
        project_id: '',
        task_id: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };
  
  // Handle delete calendar event
  const handleDeleteCalendarEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      await axios.delete(`${API}/calendar-events/${selectedEvent.id}`);
      toast.success('Event cancelled');
      setEventDetailOpen(false);
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };
  
  // Handle complete calendar event
  const handleCompleteCalendarEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      await axios.put(`${API}/calendar-events/${selectedEvent.id}`, {
        status: 'completed'
      });
      toast.success('Event marked as complete');
      setEventDetailOpen(false);
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  // Save date changes from the task detail dialog
  const handleSaveDates = async () => {
    if (!selectedTask) return;
    if (isClient) {
      toast.error('Clients cannot modify task dates');
      return;
    }
    
    try {
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        start_date: editStartDate || null,
        deadline: editDeadline,
      });
      
      toast.success('Task dates updated! Calendar and Tasks refreshed.');
      setDateEditMode(false);
      fetchData(); // Refresh calendar, tasks, dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update dates');
    }
  };

  // Handle quick date update from calendar (for admins/vendors)
  const handleQuickDateUpdate = async (newStart, newEnd) => {
    if (!selectedTask) return;
    if (isClient) {
      toast.error('Clients cannot modify task dates');
      return;
    }
    
    try {
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        start_date: moment(newStart).format('YYYY-MM-DD'),
        deadline: moment(newEnd).format('YYYY-MM-DD'),
      });
      
      toast.success('Task dates updated');
      setTaskDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Handle slot selection (clicking on date or dragging across dates) - Show choice dialog
  const handleSelectSlot = useCallback(({ start, end }) => {
    if (!isAdmin) {
      toast.error('Only admins can create items from calendar');
      return;
    }
    
    // Format dates for the forms
    const startDate = moment(start).format('YYYY-MM-DD');
    const endDate = moment(end).format('YYYY-MM-DD');
    const startDateTime = moment(start).format('YYYY-MM-DDTHH:mm');
    const endDateTime = moment(end).format('YYYY-MM-DDTHH:mm');
    
    // Store selected dates and show choice dialog
    setSelectedSlotDates({ start: startDate, end: endDate, startDateTime, endDateTime });
    setCreateChoiceOpen(true);
  }, [isAdmin]);
  
  // Handle choice selection from the choice dialog
  const handleCreateChoice = (choice) => {
    setCreateChoiceOpen(false);
    
    if (choice === 'task') {
      setCreateData({
        project_id: filterProject !== 'all' ? filterProject : '',
        title: '',
        description: '',
        start_date: selectedSlotDates.start,
        deadline: selectedSlotDates.end,
        assigned_to: '',
      });
      setCreateDialogOpen(true);
    } else if (choice === 'appointment' || choice === 'reminder') {
      setEventData({
        title: '',
        event_type: choice,
        start_datetime: selectedSlotDates.startDateTime,
        end_datetime: selectedSlotDates.endDateTime,
        project_id: filterProject !== 'all' ? filterProject : '',
        task_id: '',
        notes: '',
      });
      setSelectedEvent(null);
      setEventDialogOpen(true);
    }
  };

  // Handle create new task
  const handleCreateTask = async () => {
    if (!createData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!createData.title) {
      toast.error('Please enter a task name');
      return;
    }
    if (!createData.deadline) {
      toast.error('Please select an end date');
      return;
    }
    
    try {
      await axios.post(`${API}/tasks`, {
        project_id: createData.project_id,
        title: createData.title,
        description: createData.description || '',
        start_date: createData.start_date || null,
        deadline: createData.deadline,
        assigned_to: createData.assigned_to || null,
        status: 'not_started',
        quantity: 1,
        unit: 'No.s',
        rate: 0,
      });
      
      toast.success('Task created successfully!');
      setCreateDialogOpen(false);
      setCreateData({
        project_id: '',
        title: '',
        description: '',
        start_date: '',
        deadline: '',
        assigned_to: '',
      });
      fetchData(); // Refresh calendar, tasks page, and dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  // Handle view change (Month, Week, Day, Agenda)
  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  // Handle date navigation
  const handleNavigate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  // Status icon helper
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={16} className="text-green-600" />;
      case 'in_progress':
        return <Clock size={16} className="text-blue-600" />;
      case 'delayed':
        return <AlertCircle size={16} className="text-red-600" />;
      default:
        return <ListTodo size={16} className="text-stone-500" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="full-calendar-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <CalendarIcon className="text-red-600" />
              Project Calendar
            </h1>
            <p className="text-stone-500 mt-1">Track tasks, appointments & reminders. Click on a date to create an event.</p>
          </div>
          
          {/* Filters + Create Buttons */}
          <div className="flex gap-3 items-center">
            {/* New Appointment/Reminder Button */}
            <Button 
              onClick={() => {
                setEventData({
                  title: '',
                  event_type: 'appointment',
                  start_datetime: moment().format('YYYY-MM-DDTHH:mm'),
                  end_datetime: moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
                  project_id: filterProject !== 'all' ? filterProject : '',
                  task_id: '',
                  notes: '',
                });
                setEventDialogOpen(true);
              }}
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              data-testid="create-event-btn"
            >
              <Plus size={16} className="mr-1" />
              Appointment / Reminder
            </Button>
            
            {isAdmin && (
              <Button 
                onClick={() => {
                  setCreateData({
                    project_id: filterProject !== 'all' ? filterProject : '',
                    title: '',
                    description: '',
                    start_date: moment().format('YYYY-MM-DD'),
                    deadline: moment().add(7, 'days').format('YYYY-MM-DD'),
                    assigned_to: '',
                  });
                  setCreateDialogOpen(true);
                }}
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-task-btn"
              >
                <Plus size={16} className="mr-1" />
                New Task
              </Button>
            )}
            
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="w-[180px]" data-testid="project-filter">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="delayed">Delayed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Legend */}
        <div className="flex gap-4 p-3 bg-stone-50 rounded-lg">
          <span className="text-sm font-medium text-stone-600">Status:</span>
          {Object.entries(statusColors).map(([status, colors]) => (
            <div key={status} className="flex items-center gap-1">
              <div 
                className="w-3 h-3 rounded" 
                style={{ backgroundColor: colors.bg, border: `2px solid ${colors.border}` }}
              />
              <span className="text-xs text-stone-600 capitalize">{status.replace('_', ' ')}</span>
            </div>
          ))}
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-lg border border-stone-200 p-4" style={{ height: '70vh' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            view={currentView}
            date={currentDate}
            onView={handleViewChange}
            onNavigate={handleNavigate}
            defaultView={Views.MONTH}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            components={{
              toolbar: CustomToolbar,
            }}
            formats={calendarFormats}
            popup
            selectable={isAdmin}
            data-testid="calendar-component"
          />
        </div>

        {/* Task Detail Dialog - Click on task shows details with navigation option */}
        <Dialog open={taskDetailOpen} onOpenChange={setTaskDetailOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="task-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {getStatusIcon(selectedTask?.status)}
                Task Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedTask && (
              <div className="space-y-4">
                {/* Task Info */}
                <div className="p-4 bg-stone-50 rounded-lg">
                  <h4 className="font-semibold text-stone-800 text-lg">{selectedTask.title || selectedTask.task_title}</h4>
                  <div className="flex items-center gap-2 mt-2">
                    <Package size={14} className="text-stone-400" />
                    <span className="text-sm text-stone-500">
                      {projects.find(p => p.id === selectedTask.project_id)?.name || 'Unknown Project'}
                    </span>
                  </div>
                  {selectedTask.description && (
                    <p className="text-sm text-stone-600 mt-3 line-clamp-3">{selectedTask.description}</p>
                  )}
                </div>
                
                {/* Task Status & Dates - Editable for Admin/Vendor */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-stone-700">Schedule</span>
                    {(isAdmin || isVendor) && !dateEditMode && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setDateEditMode(true)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Pencil size={14} className="mr-1" />
                        Edit Dates
                      </Button>
                    )}
                  </div>
                  
                  {dateEditMode ? (
                    // Edit mode - show input fields
                    <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-blue-700">Start Date</Label>
                          <Input
                            type="date"
                            value={editStartDate}
                            onChange={(e) => setEditStartDate(e.target.value)}
                            className="mt-1"
                            data-testid="edit-start-date"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-red-700">End Date (Deadline) *</Label>
                          <Input
                            type="date"
                            value={editDeadline}
                            onChange={(e) => setEditDeadline(e.target.value)}
                            className="mt-1"
                            data-testid="edit-deadline"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setDateEditMode(false)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveDates} className="bg-blue-600 hover:bg-blue-700">
                          Save Dates
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode - show date cards
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600 font-medium">Start Date</p>
                        <p className="text-sm text-blue-800 mt-1">
                          {selectedTask.start_date ? formatDate(selectedTask.start_date) : 'Not set'}
                        </p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600 font-medium">End Date (Deadline)</p>
                        <p className="text-sm text-red-800 mt-1">
                          {selectedTask.deadline ? formatDate(selectedTask.deadline) : 'Not set'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-500">Current Status:</span>
                  <Badge className={`${
                    selectedTask.status === 'completed' ? 'bg-green-100 text-green-700' :
                    selectedTask.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    selectedTask.status === 'delayed' ? 'bg-red-100 text-red-700' :
                    'bg-stone-100 text-stone-600'
                  }`}>
                    {selectedTask.status?.replace('_', ' ') || 'Not Started'}
                  </Badge>
                </div>
                
                {/* Client Approval Status (if applicable) */}
                {selectedTask.client_approval_status && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} className="text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">
                        Client Approval: {selectedTask.client_approval_status}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Role-specific info */}
                {isAdmin && (
                  <div className="text-xs text-stone-400 border-t pt-3">
                    {selectedTask.rate && <p>Rate: ₹{selectedTask.rate.toLocaleString()}</p>}
                    {selectedTask.quantity && <p>Quantity: {selectedTask.quantity} {selectedTask.unit}</p>}
                  </div>
                )}
                
                {isVendor && selectedTask.progress_percentage !== undefined && (
                  <div className="text-xs text-stone-400 border-t pt-3">
                    <p>Progress: {selectedTask.progress_percentage}%</p>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setTaskDetailOpen(false)}>
                Close
              </Button>
              <Button onClick={navigateToTask} className="bg-blue-600 hover:bg-blue-700" data-testid="view-full-task-btn">
                <ExternalLink size={16} className="mr-2" />
                Open Full Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Choice Dialog - Select Task, Appointment, or Reminder */}
        <Dialog open={createChoiceOpen} onOpenChange={setCreateChoiceOpen}>
          <DialogContent className="sm:max-w-md" data-testid="create-choice-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus size={20} className="text-blue-600" />
                What would you like to create?
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 py-4">
              <p className="text-sm text-stone-500 text-center mb-4">
                {selectedSlotDates.start === selectedSlotDates.end 
                  ? `Selected date: ${moment(selectedSlotDates.start).format('DD/MM/YYYY')}`
                  : `Selected period: ${moment(selectedSlotDates.start).format('DD/MM/YYYY')} - ${moment(selectedSlotDates.end).format('DD/MM/YYYY')}`
                }
              </p>
              
              {/* Task Option */}
              <Button 
                variant="outline" 
                className="w-full h-16 flex items-center justify-start gap-4 hover:bg-blue-50 hover:border-blue-300"
                onClick={() => handleCreateChoice('task')}
                data-testid="create-choice-task"
              >
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ListTodo size={24} className="text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-stone-800">Task</p>
                  <p className="text-xs text-stone-500">Create a project task with deadlines</p>
                </div>
              </Button>
              
              {/* Appointment Option */}
              <Button 
                variant="outline" 
                className="w-full h-16 flex items-center justify-start gap-4 hover:bg-amber-50 hover:border-amber-300"
                onClick={() => handleCreateChoice('appointment')}
                data-testid="create-choice-appointment"
              >
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Users size={24} className="text-amber-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-stone-800">Appointment</p>
                  <p className="text-xs text-stone-500">Schedule a meeting or site visit</p>
                </div>
              </Button>
              
              {/* Reminder Option */}
              <Button 
                variant="outline" 
                className="w-full h-16 flex items-center justify-start gap-4 hover:bg-purple-50 hover:border-purple-300"
                onClick={() => handleCreateChoice('reminder')}
                data-testid="create-choice-reminder"
              >
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock size={24} className="text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-stone-800">Reminder</p>
                  <p className="text-xs text-stone-500">Set a personal reminder or follow-up</p>
                </div>
              </Button>
            </div>
            
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateChoiceOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Task Dialog - Click on date or drag to create */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="create-task-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus size={20} className="text-green-600" />
                Create New Task
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Project Selection */}
              <div className="space-y-2">
                <Label>Project <span className="text-red-500">*</span></Label>
                <Select value={createData.project_id} onValueChange={(v) => setCreateData({ ...createData, project_id: v })}>
                  <SelectTrigger data-testid="create-project-select">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Task Name */}
              <div className="space-y-2">
                <Label>Task Name <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Enter task name"
                  value={createData.title}
                  onChange={(e) => setCreateData({ ...createData, title: e.target.value })}
                  data-testid="create-task-name"
                />
              </div>
              
              {/* Description */}
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  placeholder="Enter task description..."
                  value={createData.description}
                  onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                  rows={3}
                  data-testid="create-task-description"
                />
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={createData.start_date}
                    onChange={(e) => setCreateData({ ...createData, start_date: e.target.value })}
                    data-testid="create-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={createData.deadline}
                    onChange={(e) => setCreateData({ ...createData, deadline: e.target.value })}
                    data-testid="create-end-date"
                  />
                </div>
              </div>
              
              {/* Assign Vendor */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users size={16} />
                  Assign Vendor (Optional)
                </Label>
                <Select value={createData.assigned_to || 'none'} onValueChange={(v) => setCreateData({ ...createData, assigned_to: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="create-vendor-select">
                    <SelectValue placeholder="Select a vendor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vendor</SelectItem>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name} ({v.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTask} className="bg-green-600 hover:bg-green-700" data-testid="create-task-submit">
                <Plus size={16} className="mr-1" />
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Create Calendar Event Dialog (Appointment/Reminder) */}
        <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
          <DialogContent className="sm:max-w-[500px]" data-testid="create-event-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon size={20} className="text-amber-600" />
                New Appointment / Reminder
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Event Type */}
              <div className="space-y-2">
                <Label>Type <span className="text-red-500">*</span></Label>
                <Select value={eventData.event_type} onValueChange={(v) => setEventData({ ...eventData, event_type: v })}>
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="appointment">📅 Appointment</SelectItem>
                    <SelectItem value="reminder">🔔 Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Title */}
              <div className="space-y-2">
                <Label>Title <span className="text-red-500">*</span></Label>
                <Input
                  placeholder={eventData.event_type === 'reminder' ? 'Follow up with client' : 'Vendor meeting'}
                  value={eventData.title}
                  onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                  data-testid="event-title-input"
                />
              </div>
              
              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date & Time <span className="text-red-500">*</span></Label>
                  <Input
                    type="datetime-local"
                    value={eventData.start_datetime}
                    onChange={(e) => setEventData({ ...eventData, start_datetime: e.target.value })}
                    data-testid="event-start-datetime"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={eventData.end_datetime}
                    onChange={(e) => setEventData({ ...eventData, end_datetime: e.target.value })}
                    data-testid="event-end-datetime"
                  />
                </div>
              </div>
              
              {/* Project (Optional) */}
              <div className="space-y-2">
                <Label>Link to Project (Optional)</Label>
                <Select value={eventData.project_id || 'none'} onValueChange={(v) => setEventData({ ...eventData, project_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="event-project-select">
                    <SelectValue placeholder="No project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">Optional - Events work independently of projects</p>
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder="Add any notes..."
                  value={eventData.notes}
                  onChange={(e) => setEventData({ ...eventData, notes: e.target.value })}
                  rows={2}
                  data-testid="event-notes-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEventDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCalendarEvent} className="bg-amber-600 hover:bg-amber-700" data-testid="create-event-submit">
                <Plus size={16} className="mr-1" />
                Create {eventData.event_type === 'reminder' ? 'Reminder' : 'Appointment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Calendar Event Detail Dialog */}
        <Dialog open={eventDetailOpen} onOpenChange={setEventDetailOpen}>
          <DialogContent className="sm:max-w-[450px]" data-testid="event-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedEvent?.event_type === 'reminder' ? '🔔' : '📅'}
                {selectedEvent?.event_type === 'reminder' ? 'Reminder' : 'Appointment'} Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedEvent && (
              <div className="space-y-4 py-4">
                <div>
                  <h3 className="font-semibold text-lg">{selectedEvent.title}</h3>
                  <Badge variant={selectedEvent.event_type === 'reminder' ? 'secondary' : 'outline'} className="mt-1">
                    {selectedEvent.event_type === 'reminder' ? 'Reminder' : 'Appointment'}
                  </Badge>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-stone-400" />
                    <span>
                      {moment(selectedEvent.start_datetime).format('DD/MM/YYYY HH:mm')}
                      {selectedEvent.end_datetime && selectedEvent.end_datetime !== selectedEvent.start_datetime && (
                        <span> - {moment(selectedEvent.end_datetime).format('HH:mm')}</span>
                      )}
                    </span>
                  </div>
                  
                  {selectedEvent.project_id && (
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-stone-400" />
                      <span>
                        {projects.find(p => p.id === selectedEvent.project_id)?.name || 'Unknown Project'}
                      </span>
                    </div>
                  )}
                  
                  {selectedEvent.notes && (
                    <div className="mt-3 p-3 bg-stone-50 rounded-lg">
                      <p className="text-stone-600">{selectedEvent.notes}</p>
                    </div>
                  )}
                  
                  <div className="text-xs text-stone-400 mt-2">
                    Created by {selectedEvent.created_by_name} on {moment(selectedEvent.created_at).format('DD/MM/YYYY')}
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCompleteCalendarEvent} className="text-green-600">
                <CheckCircle2 size={16} className="mr-1" />
                Complete
              </Button>
              <Button variant="outline" onClick={handleDeleteCalendarEvent} className="text-red-600">
                Cancel Event
              </Button>
              <Button variant="outline" onClick={() => setEventDetailOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FullCalendar;
